import { useTranslation } from 'react-i18next';
import type { AppointmentSeries } from '@shared/types/index.ts';
import type { SeriesHorizonInput } from '../../api/endpoints.ts';
import { Select, TextInput } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
import { formatShortDate } from '../../i18n/datetime.ts';

export const defaultHorizon = (): SeriesHorizonInput => ({
  horizonMode: 'company_default',
  horizonUntil: null,
  horizonCount: null,
});

export function horizonFromSeries(series: AppointmentSeries): SeriesHorizonInput {
  return {
    horizonMode: series.horizonMode ?? 'company_default',
    horizonUntil: series.horizonUntil ?? null,
    horizonCount: series.horizonCount ?? null,
  };
}

/** Calendar months clamp to the final day when that month has no matching day. */
export function horizonMaximumDate(today: string, startsOn: string): string {
  const base = [today, startsOn].sort().at(-1)!;
  const date = new Date(`${base}T12:00:00Z`);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 9);
  const end = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, end));
  return date.toISOString().slice(0, 10);
}

export function horizonValidationKey(draft: SeriesHorizonInput, startsOn: string, maxDate: string): string | null {
  if (draft.horizonMode === 'count') {
    return draft.horizonCount === null ||
      !Number.isInteger(draft.horizonCount) ||
      draft.horizonCount < 1 ||
      draft.horizonCount > 280
      ? 'recurringHorizon.countError'
      : null;
  }
  if (draft.horizonMode === 'until_date') {
    const date = draft.horizonUntil;
    return !date ||
      !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
      !Number.isFinite(Date.parse(`${date}T12:00:00Z`)) ||
      date < startsOn ||
      date > maxDate
      ? 'recurringHorizon.dateError'
      : null;
  }
  return null;
}

export function HorizonFields({
  value,
  onChange,
  companyMonths,
  startsOn,
  maxDate,
  disabled = false,
  fieldErrors = {},
}: {
  value: SeriesHorizonInput;
  onChange: (next: SeriesHorizonInput) => void;
  companyMonths: 3 | 6 | 9;
  startsOn: string;
  maxDate: string;
  disabled?: boolean;
  fieldErrors?: Record<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <Select
        label={t('recurringHorizon.howFarAhead')}
        value={value.horizonMode}
        disabled={disabled}
        error={fieldErrors.horizonMode}
        options={[
          { value: 'company_default', label: t('recurringHorizon.companyDefault', { months: companyMonths }) },
          { value: 'until_date', label: t('recurringHorizon.untilDate') },
          { value: 'count', label: t('recurringHorizon.forVisits') },
        ]}
        onChange={(event) =>
          onChange({
            horizonMode: event.target.value as SeriesHorizonInput['horizonMode'],
            horizonUntil: event.target.value === 'until_date' ? value.horizonUntil : null,
            horizonCount: event.target.value === 'count' ? value.horizonCount : null,
          })
        }
      />
      {value.horizonMode === 'until_date' ? (
        <TextInput
          label={t('recurringHorizon.bookUntil')}
          type="date"
          min={startsOn}
          max={maxDate}
          value={value.horizonUntil ?? ''}
          disabled={disabled}
          error={fieldErrors.horizonUntil}
          hint={t('recurringHorizon.dateHint', { date: formatShortDate(maxDate) })}
          onChange={(event) => onChange({ ...value, horizonUntil: event.target.value || null })}
        />
      ) : null}
      {value.horizonMode === 'count' ? (
        <NumberInput
          label={t('recurringHorizon.numberOfVisits')}
          min={1}
          max={280}
          step={1}
          value={value.horizonCount}
          disabled={disabled}
          error={fieldErrors.horizonCount}
          hint={t('recurringHorizon.countHint')}
          onValueChange={(horizonCount) => onChange({ ...value, horizonCount })}
        />
      ) : null}
      {value.horizonMode === 'company_default' ? (
        <p className="mb-4 field-hint">
          {t(companyMonths === 3 ? 'recurringHorizon.rolling84' : 'recurringHorizon.rolling', {
            months: companyMonths,
          })}
        </p>
      ) : null}
    </div>
  );
}
